import { useState, useCallback } from 'react';

export interface NotificationData {
  id: string;
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const addNotification = useCallback((notification: Omit<NotificationData, 'id'>) => {
    const id = Date.now().toString();
    const newNotification: NotificationData = {
      id,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove après la durée spécifiée
    setTimeout(() => {
      removeNotification(id);
    }, notification.duration || 3000);

    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Raccourcis pour les types de notifications
  const success = useCallback((message: string, duration?: number) => {
    return addNotification({ message, type: 'success', duration });
  }, [addNotification]);

  const error = useCallback((message: string, duration?: number) => {
    return addNotification({ message, type: 'error', duration });
  }, [addNotification]);

  const info = useCallback((message: string, duration?: number) => {
    return addNotification({ message, type: 'info', duration });
  }, [addNotification]);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    success,
    error,
    info
  };
};
